import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { IGNORE_BEFORE, RECENT_RUNS, easternDate, weeklySyncStatus, type SyncRun } from '../lib/syncstatus'
import { REZEN_SYNC_PROMPT } from '../lib/rezen-sync-prompt'

/**
 * The reZEN counterpart to `SyncStrip` — one line, same shape, same tone
 * rules, same reasons for existing (see that file's own header comment for
 * "why this exists at all" and "always mounted, shown only on the roster
 * tabs", both unchanged here). What is genuinely different is spelled out
 * below rather than assumed.
 *
 * WHY THIS IS NOT A SECOND `SyncStrip` WITH A DIFFERENT `KIND`
 *
 * `SyncStrip` pings a Chrome extension and can start a run itself with one
 * click, because the daily Metabase pull IS the extension. reZEN has no
 * extension at all — Chris, 2026-08-22: "The reZEN data is pulled manually
 * through a weekly login and agent review. We do not have access to the
 * API." The pull is a scheduled Claude session driving a real browser
 * against Bolt directly (see `claude/rezen-weekly-sync-design.md`), and
 * nothing running inside this web page can reach across into that. So there
 * is no "Update now" button that starts anything here — there is a "Copy
 * manual sync request" button, mirroring the OLD dashboard's own proven
 * pattern for this exact situation (confirmed built and working there
 * 2026-08-12): it puts the standing instructions on the clipboard, and
 * Chris pastes them into a fresh Claude chat to run the sync off-schedule.
 * `rezen-sync-prompt.ts` is the one shared source for that text — the
 * scheduled task fires the same words automatically every week.
 *
 * WHY THIS USES `weeklySyncStatus`, NOT `syncStatus`
 *
 * `syncStatus`/`watchdogVerdict` are calibrated for a job that runs every
 * WEEKDAY — `weekdaysBetween` and a holiday list. reZEN runs once a WEEK.
 * Reusing the daily math here would read a completely healthy Monday sync
 * as stale by Thursday, every single week. `weeklySyncStatus` is the
 * calendar-day-based counterpart built for exactly this cadence — see the
 * header comment on `WEEKLY CADENCE (reZEN)` in `syncstatus.ts`.
 */

const KIND = 'weekly_rezen'
const LABEL = 'reZEN profile data'

/** Same reasoning as `SyncStrip.REFRESH_MS` — a tab left open across a
 *  session boundary should not go on saying "updated today" about a day
 *  that has since passed. Less urgent for a weekly job than a daily one,
 *  but the failure mode (a stale sentence nobody notices is stale) is the
 *  same, so this stays the same interval rather than inventing a new one to
 *  justify. */
const REFRESH_MS = 5 * 60_000

/** How long "Copied" stays on the button before it reverts, so pressing it
 *  again later doesn't silently do nothing while still reading "Copied". */
const COPIED_FOR_MS = 4_000

export default function RezenStatus({ visible }: { visible: boolean }) {
  const [runs, setRuns] = useState<SyncRun[] | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)

  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const today = easternDate()

  const load = useCallback(async () => {
    // Same two-query shape as SyncStrip's `load`, same reason: a window of
    // recent rows alone is the wrong shape for "when did it last WORK" once
    // a sustained outage fills the window with failures. See that file's
    // own comment on `success` for the measured incident this avoids.
    const columns = 'id,kind,run_date,result,reason,message,stats,started_at,finished_at'
    const recent = await supabase
      .from('sync_runs')
      .select(columns)
      .eq('kind', KIND)
      .gte('run_date', IGNORE_BEFORE)
      .order('run_date', { ascending: false })
      .order('id', { ascending: false })
      .limit(RECENT_RUNS)
    if (!alive.current) return
    if (recent.error) return setReadError(recent.error.message)

    const success = await supabase
      .from('sync_runs')
      .select(columns)
      .eq('kind', KIND)
      .eq('result', 'ok')
      .gte('run_date', IGNORE_BEFORE)
      .order('run_date', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)
    if (!alive.current) return
    if (success.error) return setReadError(success.error.message)

    const rows = [...((recent.data ?? []) as SyncRun[])]
    for (const row of (success.data ?? []) as SyncRun[]) {
      if (!rows.some((r) => r.id === row.id)) rows.push(row)
    }
    setReadError(null)
    setRuns(rows)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    // Same wake-on-focus reasoning as SyncStrip: a run that finished while
    // Chris was on another tab is worth catching on return, not on the next
    // full page load.
    let last = 0
    const onWake = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - last < 1000) return
      last = now
      void load()
    }
    const timer = setInterval(onWake, REFRESH_MS)
    window.addEventListener('focus', onWake)
    document.addEventListener('visibilitychange', onWake)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', onWake)
      document.removeEventListener('visibilitychange', onWake)
    }
  }, [load])

  async function copyRequest() {
    setCopyError(null)
    try {
      await navigator.clipboard.writeText(REZEN_SYNC_PROMPT)
      if (!alive.current) return
      setCopied(true)
      setTimeout(() => {
        if (alive.current) setCopied(false)
      }, COPIED_FOR_MS)
    } catch {
      // Clipboard access can be refused by the browser (no HTTPS focus, a
      // permission denial) — this is not worth a red screen over, just a
      // plain sentence saying it did not work.
      if (alive.current) setCopyError('Could not copy — your browser may have blocked clipboard access.')
    }
  }

  if (!visible) return null

  // Same "nothing is claimed until the runs have actually been read" rule
  // as SyncStrip, same reason: a strip that says "never updated" for half a
  // second on every load is a strip that gets ignored the day it means it.
  if (runs === null && !readError) return null

  const status = weeklySyncStatus(runs ?? [], KIND, today, LABEL)
  const tone = readError ? 'warn' : status.tone

  return (
    <div className={`syncstrip ${tone}`} role="status" aria-live="polite">
      <span className="what">
        {readError ? `${LABEL} — could not check when it last updated.` : status.headline}
      </span>
      {!readError && status.stamp && <span className="when">{status.stamp}</span>}
      {(readError ?? status.detail) && (
        <span className="why">{readError ? `The last-updated check could not read its own records: ${readError}` : status.detail}</span>
      )}
      <span className="grow" />
      {copyError && <span className="why">{copyError}</span>}
      <button className="btn sm" onClick={() => void copyRequest()}>
        {copied ? 'Copied' : 'Copy manual sync request'}
      </button>
    </div>
  )
}
